import { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import { cardStyle, inputStyle, colors } from "./lib/theme";
import { computeStandings, computeHeadToHead } from "./lib/stats";

export default function ProfilePage({ user }) {
  var [users, setUsers] = useState([]);
  var [dependents, setDependents] = useState([]);
  var [matches, setMatches] = useState([]);
  var [selectedUid, setSelectedUid] = useState(user.uid);

  useEffect(function () {
    getDocs(collection(db, "users")).then(function (snap) {
      setUsers(snap.docs.map(function (d) { return { uid: d.id, ...d.data() }; }));
    });
    getDocs(collection(db, "dependents")).then(function (snap) {
      setDependents(snap.docs.map(function (d) { var data = d.data(); return { uid: data.linkedUid || d.id, displayName: data.displayName }; }));
    });
    var unsub = onSnapshot(collection(db, "matches"), function (snap) {
      setMatches(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    return unsub;
  }, []);

  var names = {};
  users.forEach(function (u) { names[u.uid] = u.displayName; });
  dependents.forEach(function (d) { if (!names[d.uid]) names[d.uid] = d.displayName; });
  function name(uid) { return names[uid] || uid; }

  var myMatches = matches
    .filter(function (m) { return (m.players || []).indexOf(selectedUid) !== -1; })
    .sort(function (a, b) { return new Date(b.timestamp) - new Date(a.timestamp); });

  var overallRows = computeStandings(myMatches, [selectedUid], names);
  var overall = overallRows.find(function (r) { return r.uid === selectedUid; })
    || { wins: 0, losses: 0, draws: 0, games: 0, winPct: -1 };

  var h2h = computeHeadToHead(myMatches, selectedUid, names);
  var mostWinsVs = h2h.slice().sort(function (a, b) { return b.wins - a.wins; })[0];
  var mostLossesVs = h2h.slice().sort(function (a, b) { return b.losses - a.losses; })[0];

  function matchInfo(m) {
    var onA = m.teamA.indexOf(selectedUid) !== -1;
    var myTeam = onA ? m.teamA : m.teamB;
    var oppTeam = onA ? m.teamB : m.teamA;
    var partner = myTeam.find(function (uid) { return uid !== selectedUid; });
    var won = m.winningTeam ? (onA ? m.winningTeam === "A" : m.winningTeam === "B") : null;
    return {
      partner: partner,
      opponents: oppTeam,
      myScore: onA ? m.scoreA : m.scoreB,
      oppScore: onA ? m.scoreB : m.scoreA,
      won: won,
    };
  }

  var seenUids = {};
  var otherUsers = users
    .filter(function (u) { return u.uid !== user.uid; })
    .concat(dependents.filter(function (d) { return d.uid !== user.uid; }))
    .filter(function (u) { if (seenUids[u.uid]) return false; seenUids[u.uid] = true; return true; })
    .sort(function (a, b) { return (a.displayName || "").localeCompare(b.displayName || ""); });

  return (
    <div>
      <h2 style={{ fontSize: 18, marginBottom: 12 }}>Player Profile</h2>

      <div style={{ marginBottom: 16 }}>
        <select style={inputStyle} value={selectedUid} onChange={function (e) { setSelectedUid(e.target.value); }}>
          <option value={user.uid}>Me ({user.displayName})</option>
          {otherUsers.map(function (u) { return <option key={u.uid} value={u.uid}>{u.displayName}</option>; })}
        </select>
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Overall Record</h3>
        <div style={{ fontSize: 28, fontWeight: 800, color: colors.teal }}>
          {overall.games > 0 ? Math.round(overall.winPct * 100) + "%" : "—"}
        </div>
        <div style={{ fontSize: 13, color: colors.textDim }}>
          {overall.games > 0
            ? overall.wins + "W – " + overall.losses + "L" + (overall.draws ? " – " + overall.draws + "D" : "") + " across " + overall.games + " games (social + league)"
            : "No games recorded yet."}
        </div>
      </div>

      {(mostWinsVs && mostWinsVs.wins > 0) || (mostLossesVs && mostLossesVs.losses > 0) ? (
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          {mostWinsVs && mostWinsVs.wins > 0 && (
            <div style={{ ...cardStyle, flex: 1 }}>
              <div style={{ fontSize: 11, color: colors.textDim, textTransform: "uppercase", letterSpacing: 1 }}>Most Wins Against</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{mostWinsVs.name}</div>
              <div style={{ fontSize: 13, color: colors.success }}>{mostWinsVs.wins + " win" + (mostWinsVs.wins === 1 ? "" : "s")}</div>
            </div>
          )}
          {mostLossesVs && mostLossesVs.losses > 0 && (
            <div style={{ ...cardStyle, flex: 1 }}>
              <div style={{ fontSize: 11, color: colors.textDim, textTransform: "uppercase", letterSpacing: 1 }}>Most Losses Against</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{mostLossesVs.name}</div>
              <div style={{ fontSize: 13, color: colors.danger }}>{mostLossesVs.losses + " loss" + (mostLossesVs.losses === 1 ? "" : "es")}</div>
            </div>
          )}
        </div>
      ) : null}

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Head-to-Head</h3>
        {h2h.length === 0 && <p style={{ fontSize: 13, color: colors.textDim }}>No opponents faced yet.</p>}
        {h2h.map(function (row) {
          return (
            <div key={row.uid} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid " + colors.border, fontSize: 13 }}>
              <span>{row.name}</span>
              <span style={{ color: colors.textDim }}>
                {row.wins + "W - " + row.losses + "L" + (row.draws ? " - " + row.draws + "D" : "") + " (" + row.games + ")"}
              </span>
            </div>
          );
        })}
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Match History</h3>
        {myMatches.length === 0 && <p style={{ fontSize: 13, color: colors.textDim }}>No matches recorded yet.</p>}
        {myMatches.map(function (m) {
          var info = matchInfo(m);
          return (
            <div key={m.id} style={{ padding: "8px 0", borderBottom: "1px solid " + colors.border, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span>{"w/ " + name(info.partner) + " vs " + info.opponents.map(name).join(" & ")}</span>
                <span style={{
                  fontWeight: 700, marginLeft: 10, whiteSpace: "nowrap",
                  color: info.won === true ? colors.success : info.won === false ? colors.danger : colors.textDim,
                }}>
                  {info.myScore + " – " + info.oppScore}
                </span>
              </div>
              <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 2 }}>
                {new Date(m.timestamp).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                {" · " + m.venueName + " · " + (m.mode === "league" ? "League" : "Social")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
